/**
 * Commenting on a passage of a document (RFC 0289 S5).
 *
 * THE POINT OF THIS SLICE is that it adds no comment model. Selecting a
 * sentence and saying something about it writes an ordinary `CommentEntry`
 * anchored `{ documentId, anchorText }`, into the same project map the review
 * app and the agent's `comment` tool write to, read back by the same
 * `projectThreads`, and rendered by the same `CommentThreadView` the side
 * column uses. The editor and the review are one feature after this, not two
 * that resemble each other.
 *
 * WHY A DOM SELECTION RATHER THAN THE EDITOR'S OWN. The markdown pane has two
 * modes: a rendered `ReactMarkdown` tree and a ProseMirror editor over the text
 * CRDT. A selection API reaching into ProseMirror would work in one of them and
 * leave the read mode, which is the mode most documents are opened in, with no
 * way to comment at all. `window.getSelection()` is the one thing both modes
 * agree on.
 *
 * WHY THE ANCHOR IS THE TEXT AND NOT A CURSOR. RFC 0289 §8 made this trade
 * knowingly: System A's `AnchorPoint` carried Automerge cursors and survived
 * edits precisely, but only the document editor could read one. `anchorText` is
 * a heuristic that every surface understands, which is what makes one comment
 * model possible at all. A passage that is edited away leaves its comment on
 * text that no longer appears, the same staleness the review app already shows
 * for a moved hunk.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  answerComment,
  type Candidate,
  type CommentAuthor,
  type CommentEntry,
  type CommentThread,
  createComment,
  deliverMentions,
  type Mention,
  projectAuthor,
  projectCandidates,
  projectThreads,
  resolveComment,
} from '@mod/core/model'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useCommentMap } from '../../services/comments/useCommentMap'
import { commentAuthor } from '../../services/identity/commentAuthor'
import {
  type CommentReader,
  commentReader,
  type ThreadWaiting,
  threadWaiting,
} from '../../services/sessions/commentReader'
import { labelFrom, useSelection } from '../../services/sessions/selection'
import {
  routeComment,
  useSpaceSession,
} from '../../services/sessions/spaceSession'
import { useDocument } from '../../services/world/repo/useDocument'
import {
  CommentThreadView,
  ReaderLine,
  sendFailureMessage,
} from './CommentThreadView'
import { MentionComposer } from './MentionComposer'
import { EMPTY_DRAFT, type MentionDraft, verifyMentions } from './mentionDraft'

/**
 * How much of a selection becomes the anchor.
 *
 * BOUNDED because the anchor is also the GROUPING KEY (RFC 0289 §7): a comment
 * on a whole document selected with cmd-A would otherwise mint a key the length
 * of the document, which no second comment can ever match and which renders as
 * a heading the size of the pane. Two selections sharing a first 120 characters
 * land in one group, which is the right answer far more often than two blocks
 * headed by the same sentence.
 */
export const ANCHOR_MAX = 120

/**
 * The anchor for a selected passage: whitespace collapsed, trimmed, bounded.
 *
 * COLLAPSED because the same sentence selected in the rendered view and in the
 * editor arrives with different internal whitespace (markdown source wraps,
 * rendered HTML does not), and an anchor that depends on which mode you were in
 * would file the two comments under different headings while a reader sees one
 * sentence. Returns '' for a selection with no text in it, which the caller
 * treats as "no anchor" rather than as an anchor of nothing.
 */
export function anchorTextFrom(raw: string): string {
  const collapsed = raw.replace(/\s+/g, ' ').trim()
  if (!collapsed) return ''
  return collapsed.length > ANCHOR_MAX
    ? collapsed.slice(0, ANCHOR_MAX).trimEnd()
    : collapsed
}

/** Where the popover goes, in coordinates relative to the wrapper. */
interface Spot {
  text: string
  top: number
  left: number
}

/**
 * Wraps a document pane and adds commenting on a selection.
 *
 * `documentId` MUST be the id the side column is scoped to, so a comment made
 * here appears there. The caller passes the subject rather than the content
 * doc: those coincide for a plain markdown document and diverge for an app
 * instance, and a comment filed against the wrong one of the two is invisible
 * in the column with nothing to explain why.
 */
export function DocumentComments({
  projectId,
  documentId,
  className = 'relative min-h-0 flex-1 overflow-y-auto',
  disabled = false,
  children,
}: {
  projectId: string
  documentId: string
  /**
   * Stand down: render the pane and add nothing to it.
   *
   * For an editor that does its own commenting (see `claimComments` in
   * App). Both surfaces write the same comments to the same catalog,
   * so having both was not broken so much as DOUBLED: two Comment buttons on
   * one selection, and a passage commented on through the popover answered in a
   * card that looked nothing like it.
   *
   * The wrapper stays in the tree rather than being conditionally rendered,
   * because it is also the pane's layout: dropping it would reflow the editor
   * the moment the claim arrived.
   */
  disabled?: boolean
  /**
   * The wrapper's layout. Defaults to being the SCROLL CONTAINER, which is what
   * the popover's positioning assumes: coordinates are taken relative to this
   * element and offset by its `scrollTop`, so a popover sticks to its passage
   * as the document scrolls.
   *
   * A pane that manages its own scrolling (a host-realm editor app) should pass
   * a non-scrolling class instead. The popover is then placed correctly when it
   * opens and does not follow that pane's internal scrolling, which is a
   * smaller loss than having no way to comment at all.
   */
  className?: string
  children: ReactNode
}) {
  const repo = useRepo()
  const space = useSpaceSession()
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  // This page's comments where they live (RFC 0339).
  const commentMap = useCommentMap(
    projectId,
    useMemo(() => [documentId], [documentId]),
  )
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [spot, setSpot] = useState<Spot | null>(null)
  // Through a ref so the watcher is not torn down and rebuilt whenever the
  // provider's value changes, which would drop a selection mid-drag.
  const { select } = useSelection()
  const selectRef = useRef(select)
  selectRef.current = select
  const [open, setOpen] = useState<Spot | null>(null)

  // The selection, watched on the document because a drag can end outside the
  // pane. Anything selected outside this wrapper is somebody else's selection.
  useEffect(() => {
    // WATCHED EVEN WHEN COMMENTING IS DISABLED (RFC 0297 §4e). `disabled` means
    // the editor draws its own Comment button, which is a reason not to draw a
    // second popover and NOT a reason to stop knowing what is selected: the
    // holdings bar reports the passage whoever drew the button. Only the
    // popover is gated below.
    const read = () => {
      const wrap = wrapRef.current
      const sel = window.getSelection()
      if (!wrap || !sel || sel.isCollapsed || sel.rangeCount === 0) {
        setSpot(null)
        return
      }
      const range = sel.getRangeAt(0)
      if (!wrap.contains(range.commonAncestorContainer)) {
        setSpot(null)
        return
      }
      const text = anchorTextFrom(sel.toString())
      if (!text) {
        setSpot(null)
        return
      }
      // The bar's copy is sticky, so a null here is ignored by the provider and
      // the passage survives the click into the composer.
      selectRef.current({ documentId, label: labelFrom(sel.toString()) })
      if (disabled) {
        setSpot(null)
        return
      }
      const box = range.getBoundingClientRect()
      const base = wrap.getBoundingClientRect()
      setSpot({
        text,
        top: box.bottom - base.top + wrap.scrollTop,
        left: box.left - base.left,
      })
    }
    document.addEventListener('selectionchange', read)
    return () => document.removeEventListener('selectionchange', read)
  }, [disabled, documentId])

  // Threads already written against the open popover's passage. Filtered by
  // the SAME subset match every other surface uses, so an agent's comment on
  // this sentence shows up here without the editor knowing an agent exists.
  const threads: CommentThread[] = useMemo(() => {
    if (!open) return []
    return projectThreads(commentMap, {
      documentId,
      anchorText: open.text,
    })
  }, [commentMap, documentId, open])

  // Who wrote each comment, named in one place (RFC 0324 3a).
  const authorOf = useCallback(
    (c: CommentEntry): CommentAuthor => projectAuthor(projectDoc, c),
    [projectDoc],
  )

  // Who a draft would reach, said before it is sent (RFC 0324 3c).
  const readerFor = useCallback(
    (mentions: Mention[], replyTo?: string) =>
      commentReader({
        projectDoc,
        comments: commentMap,
        mentions,
        ...(replyTo ? { replyTo } : {}),
        space: { sessionId: space.sessionId, canStart: Boolean(space.start) },
      }),
    [projectDoc, commentMap, space],
  )

  // What each thread is waiting for, off the registry (RFC 0324 3d).
  const waitingFor = useCallback(
    (t: CommentThread) =>
      threadWaiting({
        projectDoc,
        comments: commentMap,
        thread: t,
        spaceSessionId: space.sessionId,
      }),
    [projectDoc, commentMap, space.sessionId],
  )

  const candidates = useCallback(
    (query: string): Candidate[] => projectCandidates(projectDoc, query),
    [projectDoc],
  )

  const onReply = useCallback(
    async (parentId: string, body: string, mentions: Mention[]) => {
      const author = await commentAuthor(repo, projectId)
      const out = await answerComment(repo, projectId, {
        parentId,
        author,
        body,
        ...(mentions.length > 0 ? { mentions } : {}),
      })
      if (mentions.length > 0) {
        await deliverMentions(repo, projectId, {
          commentId: out.id,
          mentions,
          author,
          body,
          documentId,
        })
      }
    },
    [repo, projectId, documentId],
  )

  const onResolve = useCallback(
    async (commentId: string, resolved: boolean) => {
      await resolveComment(repo, projectId, commentId, resolved)
    },
    [repo, projectId],
  )

  if (disabled) {
    return (
      <div ref={wrapRef} className={className}>
        {children}
      </div>
    )
  }

  return (
    <div ref={wrapRef} className={className}>
      {children}
      {spot && !open && (
        <button
          type="button"
          // Mouse DOWN, not click: clicking clears the selection first, and by
          // the time a click handler ran there would be no passage to anchor to.
          onMouseDown={(e) => {
            e.preventDefault()
            setOpen(spot)
            setSpot(null)
          }}
          style={{ top: spot.top + 4, left: spot.left }}
          className="absolute z-20 rounded border border-border bg-background px-2 py-1 text-[11px] text-foreground shadow-sm hover:bg-muted"
        >
          Comment
        </button>
      )}
      {open && (
        <PassagePopover
          spot={open}
          threads={threads}
          onClose={() => setOpen(null)}
          onReply={onReply}
          onResolve={onResolve}
          candidates={candidates}
          authorOf={authorOf}
          readerFor={readerFor}
          waitingFor={waitingFor}
          onCreate={async (body, mentions) => {
            const author = await commentAuthor(repo, projectId)
            const commentId = await createComment(repo, projectId, {
              target: { documentId, anchorText: open.text },
              author,
              body,
              ...(mentions.length > 0 ? { mentions } : {}),
            })
            // THE CASE RFC 0292 EXISTS FOR. A comment that starts a thread has
            // no parent, so RFC 0289's delivery had nobody to wake and a
            // question typed on a passage reached no one. Naming a session is
            // how the person says who it is for.
            if (mentions.length > 0) {
              await deliverMentions(repo, projectId, {
                commentId,
                mentions,
                author,
                body,
                documentId,
                anchorText: open.text,
              })
            } else {
              // RFC 0301 §3d: naming nobody sends it to the space's session.
              await routeComment(repo, projectId, space, {
                id: commentId,
                body,
                documentId,
              })
            }
          }}
        />
      )}
    </div>
  )
}

/** The passage, what has been said about it, and a box to say something. */
function PassagePopover({
  spot,
  threads,
  onClose,
  onCreate,
  onReply,
  onResolve,
  candidates,
  authorOf,
  readerFor,
  waitingFor,
}: {
  spot: Spot
  threads: CommentThread[]
  onClose: () => void
  onCreate: (body: string, mentions: Mention[]) => Promise<void>
  onReply: (
    parentId: string,
    body: string,
    mentions: Mention[],
  ) => Promise<void>
  onResolve: (commentId: string, resolved: boolean) => Promise<void>
  candidates: (query: string) => Candidate[]
  authorOf: (c: CommentEntry) => CommentAuthor
  readerFor: (mentions: Mention[], replyTo?: string) => CommentReader
  waitingFor: (t: CommentThread) => ThreadWaiting | null
}) {
  const [draft, setDraft] = useState<MentionDraft>(EMPTY_DRAFT)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const now = Date.now()
  const save = async () => {
    const body = draft.text.trim()
    if (!body || saving) return
    setSaving(true)
    setError(null)
    try {
      await onCreate(body, verifyMentions(draft.text, draft.mentions))
      setDraft(EMPTY_DRAFT)
      // Left OPEN on success, showing the comment that was just made under the
      // passage it is about. Closing would be the same motion as a failed save
      // and give a person no way to tell the two apart.
    } catch (err) {
      setError(sendFailureMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{ top: spot.top + 4, left: spot.left }}
      className="absolute z-20 flex w-72 flex-col gap-1.5 rounded border border-border bg-background p-2 shadow-md"
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="truncate text-[11px] text-muted-foreground/70 italic">
          {spot.text}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-[10px] text-muted-foreground/60 uppercase hover:text-foreground/80"
        >
          Close
        </button>
      </div>
      {threads.length > 0 && (
        <ul className="flex max-h-64 flex-col gap-px overflow-y-auto">
          {threads.map((t) => (
            <CommentThreadView
              key={t.root.id}
              thread={t}
              now={now}
              onReply={onReply}
              onResolve={onResolve}
              candidates={candidates}
              authorOf={authorOf}
              readerFor={readerFor}
              waitingFor={waitingFor}
            />
          ))}
        </ul>
      )}
      {/* Focused on mount: the popover exists because somebody just asked to
          write, and making them click once more to type is the friction that
          stops people commenting at all. Escape closes the popover, EXCEPT
          while the `@` picker is open, where it closes the picker — dismissing
          a dropdown must not throw away the sentence. */}
      <MentionComposer
        draft={draft}
        onDraftChange={setDraft}
        onSubmit={() => void save()}
        onEscape={onClose}
        candidates={candidates}
        placeholder={threads.length > 0 ? 'Add a comment…' : 'Comment…'}
        rows={2}
        disabled={saving}
        autoFocus
        className="w-full resize-y rounded border border-border/60 bg-background px-1.5 py-1 text-[12px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-border"
      />
      <ReaderLine reader={readerFor(draft.mentions)} />
      {error && (
        <p className="text-[10px] text-destructive/80" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
